import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Popover } from 'antd';
import { LuInfo } from 'react-icons/lu';

/**
 * A section heading's info button: the explanation that used to sit under the section, behind one click (the
 * Details chip in the player bar is the same shape). Click, not hover, so it opens on touch and from the keyboard.
 * antd's Popover does not close on Escape by itself, so the key is caught on the document while it is open, and
 * stopped there: the page's own Escape (leave correcting mode) must not fire for a key that only closed this.
 */

const Help = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.token.marginXS}px;
	max-width: ${({ theme }) => theme.token.controlHeight * 9}px;
	font-size: ${({ theme }) => theme.token.fontSizeSM}px;
	line-height: ${({ theme }) => theme.token.lineHeightSM};
	color: ${({ theme }) => theme.token.colorTextSecondary};
	p {
		margin: 0;
	}
`;

// label: what the button is for, to a screen reader and as the popover's title; paragraphs: plain strings
const InfoPopover = ({ label, title = label, paragraphs, placement = 'left' }) => {
	const [open, setOpen] = useState(false);
	useEffect(() => {
		if (!open) return;
		const onKey = e => {
			if (e.key !== 'Escape') return;
			e.stopPropagation();
			setOpen(false);
		};
		document.addEventListener('keydown', onKey, true);
		return () => document.removeEventListener('keydown', onKey, true);
	}, [open]);
	if (!paragraphs?.length) return null;
	return (
		<Popover
			trigger="click"
			open={open}
			onOpenChange={next => setOpen(next)}
			placement={placement}
			title={title}
			content={
				<Help>
					{paragraphs.map(p => (
						<p key={p}>{p}</p>
					))}
				</Help>
			}>
			<Button
				type="text"
				size="small"
				icon={<LuInfo />}
				aria-label={label}
				title={label}
				aria-expanded={open}
			/>
		</Popover>
	);
};

export default InfoPopover;
